/**
 * 設定情報シート（印刷して人に渡す紙）の描画。
 *
 * ## なぜ画面用のコンポーネントを流用しないのか
 *
 * 紙とディスプレイでは制約が違う。
 *
 *   1. **背景色は既定で印刷されない。** ブラウザの「背景のグラフィック」は
 *      既定オフなので、色で意味を持たせた設計はそのまま白く抜ける。
 *      `print-color-adjust: exact` を指定してもユーザーが切れる設定なので、
 *      **色に依存しない**（枠線・太字・見出しの語で区別する）ことを前提にする。
 *   2. **ダークテーマをそのまま刷らせない。** 契約者パネルはテーマに追随するが、
 *      黒地の紙はトナーを使い切るうえ読めない。シートの中だけは常に
 *      白地・黒文字に固定する。
 *   3. アプリのナビゲーション・ボタンは紙に要らない。`@media print` で落とす。
 *
 * そのため、ここでは `@mail-sv/ui` のカードを使わず、**このシート専用の
 * 素の CSS** を持つ。クラス名はすべて `sheet-` 前置きで、他画面に漏れない。
 *
 * ## パスワードは印字しない
 *
 * 紙は再発行できるものにする。パスワードを載せると再発行のたびに紙が増え、
 * 古い紙が残る（API もそもそも値を返さない）。「管理者が設定した値」とだけ書く。
 */
import * as React from 'react';
import { formatQuota } from '../lib/format.js';
import type { SetupSheet } from '../lib/api-types.js';

/**
 * シート専用のスタイル。ページに 1 回だけ置く。
 *
 * **`<style>` は CSP で許されている**（`style-src 'self' 'unsafe-inline'`）。
 * インラインの `<script>` とは扱いが違うので、ここに置いてよい。
 */
export function SetupSheetStyles(): React.JSX.Element {
  return (
    <style>{`
.sheet-page {
  /* 紙面は常に白地・黒文字。アプリのテーマ変数を参照しない。 */
  background: #ffffff;
  color: #111111;
  max-width: 190mm;
  margin: 0 auto 16mm;
  padding: 12mm 10mm;
  border: 1px solid #cccccc;
  font-family: system-ui, -apple-system, 'Segoe UI', 'Hiragino Sans', 'Noto Sans JP', sans-serif;
  font-size: 10.5pt;
  line-height: 1.6;
}
.sheet-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 8mm; }
.sheet-title { font-size: 15pt; font-weight: 700; margin: 0 0 2mm; }
.sheet-brand { font-size: 9pt; color: #444444; text-align: right; white-space: nowrap; }
.sheet-address {
  font-family: ui-monospace, 'SFMono-Regular', Consolas, monospace;
  font-size: 13pt;
  font-weight: 700;
  word-break: break-all;
}
.sheet-section { margin-top: 6mm; }
.sheet-section h2 {
  font-size: 11pt;
  font-weight: 700;
  margin: 0 0 2mm;
  padding-bottom: 1mm;
  border-bottom: 2px solid #111111;
}
.sheet-kv { width: 100%; border-collapse: collapse; }
.sheet-kv th {
  text-align: left;
  width: 38mm;
  padding: 1.5mm 2mm 1.5mm 0;
  font-weight: 600;
  vertical-align: top;
  white-space: nowrap;
}
.sheet-kv td {
  padding: 1.5mm 0;
  vertical-align: top;
  word-break: break-all;
  font-family: ui-monospace, 'SFMono-Regular', Consolas, monospace;
}
.sheet-table { width: 100%; border-collapse: collapse; }
.sheet-table th, .sheet-table td {
  border: 1px solid #999999;
  padding: 1.5mm 2mm;
  text-align: left;
  vertical-align: top;
}
.sheet-table th { font-weight: 700; }
.sheet-table td.sheet-num { text-align: right; font-variant-numeric: tabular-nums; }
.sheet-mono { font-family: ui-monospace, 'SFMono-Regular', Consolas, monospace; word-break: break-all; }
.sheet-note { font-size: 9pt; color: #333333; margin: 2mm 0 0; }
.sheet-callout {
  margin-top: 3mm;
  border: 2px solid #111111;
  padding: 2.5mm 3mm;
  font-weight: 600;
}
.sheet-foot {
  margin-top: 6mm;
  padding-top: 2mm;
  border-top: 1px solid #999999;
  font-size: 8.5pt;
  color: #444444;
  display: flex;
  justify-content: space-between;
  gap: 6mm;
}
.sheet-advanced { margin-top: 3mm; }
.sheet-advanced summary { font-size: 9.5pt; cursor: pointer; }

@media print {
  /* 印刷では画面用の枠と余白を消し、1 枚 = 1 アドレスにする。 */
  .sheet-page { border: 0; margin: 0; padding: 0; max-width: none; page-break-after: always; }
  .sheet-page:last-of-type { page-break-after: auto; }
  .sheet-noprint { display: none !important; }
  /* 詳細（上級者向け）は畳んでいても紙には出す。紙は開けない。 */
  .sheet-advanced > summary { display: none; }
  .sheet-advanced > *:not(summary) { display: block !important; }
  /* 背景を使う要素は無いが、枠線が飛ばないよう明示しておく。 */
  .sheet-table, .sheet-callout { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
}
@page { size: A4; margin: 14mm; }
`}</style>
  );
}

/**
 * 1 アドレスぶんのシート。
 *
 * `brandName` はヘッダの発行元表示に使う。空でも紙として成立するよう、
 * 値が無ければその行ごと出さない。
 */
export function SetupSheetCard({
  sheet,
  brandName,
  includeSettings = true,
}: {
  sheet: SetupSheet;
  brandName: string;
  /**
   * 「メールソフトに入力する値」を紙に載せるか。既定は載せる。
   *
   * **外すことに意味がある。** この節にはサーバー名・ポート・ユーザー名が
   * 並ぶ。本人に渡す控えなら要るが、席に置く / 名簿に綴じるといった
   * 「アドレスだけ分かればよい」使い方では、接続情報まで紙で持ち歩かせる
   * 必要がない。**紙は回収も失効もできない**ので、要らない情報は
   * 最初から載せないほうがよい。
   */
  includeSettings?: boolean;
}): React.JSX.Element {
  const recommended = sheet.connections.filter((c) => c.recommended);
  const advanced = sheet.connections.filter((c) => !c.recommended);

  return (
    <article className="sheet-page">
      <header className="sheet-head">
        <div>
          <p className="sheet-title">メール設定情報</p>
          <p className="sheet-address">{sheet.address}</p>
          {sheet.displayName ? <p className="sheet-note">{sheet.displayName}</p> : null}
        </div>
        <div className="sheet-brand">
          {brandName ? <div>{brandName}</div> : null}
          <div>発行日: {new Date(sheet.issuedAt).toLocaleDateString('ja-JP')}</div>
        </div>
      </header>

      {/*
        **ユーザー名の説明を最初に置く。** 設定の失敗の大半は
        「@ より前だけを入れた」なので、表の中の 1 行にすると読み飛ばされる。
      */}
      <div className="sheet-callout">
        ユーザー名（ログイン ID）は <span className="sheet-mono">{sheet.username}</span>{' '}
        です。@ より前だけではログインできません。
      </div>

      <section className="sheet-section">
        <h2>基本情報</h2>
        <table className="sheet-kv">
          <tbody>
            <tr>
              <th scope="row">メールアドレス</th>
              <td>{sheet.address}</td>
            </tr>
            <tr>
              <th scope="row">ユーザー名</th>
              <td>{sheet.username}</td>
            </tr>
            <tr>
              <th scope="row">パスワード</th>
              {/*
                **印字しない。** 値を載せると再発行のたびに紙が増え、
                古い紙が残る。ここは説明だけにする。
              */}
              <td className="sheet-mono">管理者が設定した値（この用紙には記載しません）</td>
            </tr>
            <tr>
              <th scope="row">メールサーバー</th>
              <td>{sheet.host}</td>
            </tr>
            <tr>
              <th scope="row">保存できる容量</th>
              <td>{formatQuota(sheet.quotaBytes)}</td>
            </tr>
            {sheet.webmailUrl ? (
              <tr>
                <th scope="row">Webメール</th>
                <td>{sheet.webmailUrl}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>

      {/* 選択で外せる。理由は includeSettings の説明。 */}
      {includeSettings ? (
      <section className="sheet-section">
        <h2>メールソフトに入力する値</h2>
        {recommended.length === 0 ? (
          <p className="sheet-note">
            このメールアドレスでは、メールソフトからの送受信が有効になっていません。
            管理者にお問い合わせください。
          </p>
        ) : (
          <table className="sheet-table">
            <thead>
              <tr>
                <th scope="col">用途</th>
                <th scope="col">種類</th>
                <th scope="col">サーバー名</th>
                <th scope="col">ポート</th>
                <th scope="col">暗号化</th>
              </tr>
            </thead>
            <tbody>
              {recommended.map((c) => (
                <tr key={`${c.protocol}-${c.port}`}>
                  <td>{c.purpose}</td>
                  <td>{c.protocol}</td>
                  <td className="sheet-mono">{c.host}</td>
                  <td className="sheet-num">{c.port}</td>
                  <td>{c.encryption}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {advanced.length > 0 ? (
          <details className="sheet-advanced">
            <summary>別のポートを使う場合（上級者向け）</summary>
            <table className="sheet-table" style={{ marginTop: '2mm' }}>
              <thead>
                <tr>
                  <th scope="col">用途</th>
                  <th scope="col">種類</th>
                  <th scope="col">ポート</th>
                  <th scope="col">暗号化</th>
                </tr>
              </thead>
              <tbody>
                {advanced.map((c) => (
                  <tr key={`${c.protocol}-${c.port}`}>
                    <td>{c.purpose}</td>
                    <td>{c.protocol}</td>
                    <td className="sheet-num">{c.port}</td>
                    <td>{c.encryption}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        ) : null}

        <p className="sheet-note">
          {sheet.autoconfigReady
            ? 'Outlook・Thunderbird では、メールアドレスとパスワードを入力するだけで上の値が自動で設定されます（自動設定に対応しています）。うまくいかない場合だけ、上の値を手で入力してください。'
            : '自動設定には対応していません。上の値を手で入力してください。'}
        </p>
      </section>
      ) : null}

      <footer className="sheet-foot">
        <span>
          パスワードはこの用紙に含まれません。忘れた場合は管理者が再設定します。
        </span>
        <span className="sheet-mono">{sheet.domainName}</span>
      </footer>
    </article>
  );
}
